import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { BookOpen, ExternalLink, ArrowLeft, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '';

// Regional Service cards are a compact preview — render the description as PLAIN TEXT (rich HTML
// stripped) so formatting can never break the card layout and raw tags are never exposed.
const toPreviewText = (s) => DOMPurify
  .sanitize(s || '', { ALLOWED_TAGS: [], ALLOWED_ATTR: [] })
  .replace(/\s+/g, ' ')
  .trim();

export default function EmployerResources() {
  const [user, setUser] = useState(null);
  const [guides, setGuides] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'services' ? 'services' : 'guides';

  useEffect(() => {
    (async () => {
      try {
        const [u, g, r] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true }),
          axios.get(`${BACKEND_URL}/api/platform/content?type=resource`, { withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/regional/resources`, { withCredentials: true }).catch(() => ({ data: [] })),
        ]);
        setUser(u.data); setGuides(g.data || []); setServices(r.data || []);
      } catch (e) { /* empty states */ } finally { setLoading(false); }
    })();
  }, []);

  return (
    <Layout user={user}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => navigate('/employer/dashboard')} data-testid="resources-back-dashboard"
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-brand mb-4 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </button>
        <div className="flex items-center gap-3 mb-6">
          <span className="w-10 h-10 rounded-lg bg-brand-light flex items-center justify-center flex-shrink-0">
            <BookOpen className="w-5 h-5 text-brand" />
          </span>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 leading-tight">Resources</h1>
            <p className="text-sm text-slate-500">Practical employer guides and local services available to your business.</p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand" /></div>
        ) : (
          <Tabs value={tab} onValueChange={(v) => setParams(v === 'services' ? { tab: 'services' } : {}, { replace: true })}>
            <TabsList className="bg-slate-100 rounded-lg p-1 mb-6">
              <TabsTrigger value="guides" data-testid="res-tab-guides" className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm px-4">Employer Guides</TabsTrigger>
              <TabsTrigger value="services" data-testid="res-tab-services" className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm px-4">Regional Services</TabsTrigger>
            </TabsList>

            {/* Employer Guides — global article library */}
            <TabsContent value="guides">
              {guides.length === 0 ? (
                <Card className="p-12 text-center border border-slate-200" data-testid="guides-empty">
                  <BookOpen className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-700 font-medium">Employer guides are coming soon.</p>
                  <p className="text-sm text-slate-400 mt-1">Practical hiring guides will appear here.</p>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="guides-grid">
                  {guides.map((a) => (
                    <button key={a.id} onClick={() => navigate(`/employer/article/resource/${a.id}`)} data-testid={`guide-card-${a.id}`}
                      className="group text-left bg-white rounded-xl border border-slate-200 hover:border-brand/40 hover:shadow-md transition-all overflow-hidden flex flex-col">
                      {a.featured_image && (
                        <div className="h-36 w-full bg-slate-100 overflow-hidden">
                          <img src={a.featured_image} alt="" className="w-full h-full object-cover" />
                        </div>
                      )}
                      <div className="p-5 flex flex-col flex-1">
                        {a.published_at && <p className="text-xs text-slate-400 mb-1">{fmtDate(a.published_at)}</p>}
                        <h3 className="text-base font-semibold text-slate-900 group-hover:text-brand transition-colors leading-snug">{a.title}</h3>
                        {a.excerpt && <p className="text-sm text-slate-600 mt-2 line-clamp-3 flex-1">{a.excerpt}</p>}
                        <span className="inline-flex items-center gap-1 text-sm font-medium text-brand mt-3">Read guide <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" /></span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Regional Services — existing external-link directory (unchanged behavior) */}
            <TabsContent value="services">
              {services.length === 0 ? (
                <Card className="p-12 text-center border border-slate-200" data-testid="resources-empty">
                  <BookOpen className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-700 font-medium">No regional services have been added yet.</p>
                  <p className="text-sm text-slate-400 mt-1">Your regional team hasn't added any services.</p>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="resources-grid">
                  {services.map((r) => (
                    <a key={r.id} href={r.url} target="_blank" rel="noopener noreferrer" data-testid={`resource-card-${r.id}`}
                      className="group bg-white rounded-xl border border-slate-200 hover:border-brand/40 hover:shadow-md transition-all p-5 flex flex-col">
                      {r.category && (
                        <span className="self-start text-[10px] font-semibold uppercase tracking-wide bg-brand-light text-brand px-2 py-0.5 rounded-full mb-2">{r.category}</span>
                      )}
                      <h3 className="text-base font-semibold text-slate-900 group-hover:text-brand transition-colors leading-snug">{r.title}</h3>
                      {r.description && <p className="text-sm text-slate-600 mt-2 line-clamp-3 flex-1">{toPreviewText(r.description)}</p>}
                      <span className="inline-flex items-center gap-1 text-sm font-medium text-brand mt-3">
                        Visit service <ExternalLink className="w-3.5 h-3.5" />
                      </span>
                    </a>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>
    </Layout>
  );
}
